import React, { memo } from "react"

import { type CriterionValue } from "@/constants/criterion-values"
import { useTableFocusSafe } from "@/human-feedback/focus"
import { yesNoBadgeShapeClasses } from "@/metrics/metric-display"

import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { useCriterionRowInteractions } from "../../hooks/useCriterionRowInteractions"
import { useCriterionVoting } from "../../hooks/useCriterionVoting"
import {
  CRITERION_DISPLAY,
  CRITERION_VALUES,
  getCriterionBadgeClass,
} from "./composite-evaluation-types"

interface CompositeEvaluationSubRowsProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  projectId?: string
  metricOutput?: RunMetricOutput
}

export const CompositeEvaluationSubRows: React.FC<CompositeEvaluationSubRowsProps> =
  memo(
    ({
      metricId,
      simulationOutputId,
      annotationsForMetric,
      projectId,
      metricOutput,
    }: CompositeEvaluationSubRowsProps) => {
      const tableFocus = useTableFocusSafe()

      const { criteria, selectedValues, handleCriterionChange, isReadOnly } =
        useCriterionVoting({
          metricId,
          simulationOutputId,
          annotationsForMetric,
          metricOutput,
          projectId,
        })

      const { expandedCriteria, handleSubRowKeyDown } =
        useCriterionRowInteractions({
          onCriterionChange: handleCriterionChange,
        })

      const unselectedClasses = `${yesNoBadgeShapeClasses} w-12 cursor-pointer bg-surface-1 text-coval-text-muted hover:bg-surface-1`

      if (criteria.length === 0) {
        return null
      }

      return (
        <>
          {criteria.map((criterion, index) => {
            const currentValue = selectedValues?.[index] || ""
            const aiResult = criterion.result
            const isExpanded = expandedCriteria.has(index)

            return (
              <tr
                key={`${metricId}-criterion-${index}`}
                className="metric-row subrow-focusable border-b border-coval-border/50 bg-surface-0/50 focusable-row hover:bg-surface-1 focus:outline-none focus:border-l-2 focus:border-r-2 focus:border-l-coval-border-active focus:border-r-coval-border-active"
                tabIndex={0}
                aria-label={`Criterion ${index + 1}: ${criterion.criterion}`}
                onFocus={() => tableFocus?.setSelectedMetricId(metricId)}
                onClick={() => tableFocus?.setSelectedMetricId(metricId)}
                onKeyDown={(e) => handleSubRowKeyDown(e, index)}
              >
                {/* Criterion text (Metric column) */}
                <td
                  className="py-2 pl-8 pr-4 align-top"
                  style={{ width: "40%" }}
                  data-metric-id={metricId}
                >
                  <span
                    className={`criterion-text text-xs text-coval-text-muted leading-tight ${isExpanded ? "" : "line-clamp-2"}`}
                  >
                    {index + 1}. {criterion.criterion}
                  </span>
                </td>

                {/* AI result badge (Metric Output column) */}
                <td
                  className="py-2 px-4 align-top text-center"
                  style={{ width: "10%" }}
                >
                  <span
                    className={`${yesNoBadgeShapeClasses} ${getCriterionBadgeClass(aiResult)}`}
                  >
                    {CRITERION_DISPLAY[aiResult]?.label ?? "N/A"}
                  </span>
                </td>

                {/* Human review buttons (Human Review column) */}
                <td className="py-2 px-4 align-top" style={{ width: "25%" }}>
                  <div className="flex justify-center">
                    {currentValue ? (
                      <button
                        type="button"
                        disabled={isReadOnly}
                        className={`${yesNoBadgeShapeClasses} ${getCriterionBadgeClass(currentValue as CriterionValue)} cursor-pointer`}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCriterionChange(
                            index,
                            currentValue as CriterionValue,
                          )
                        }}
                        aria-label={`Clear criterion ${index + 1} selection`}
                      >
                        {CRITERION_DISPLAY[currentValue as CriterionValue]
                          ?.label ?? currentValue}
                      </button>
                    ) : (
                      <div className="flex text-xs space-x-2 items-center justify-center">
                        {CRITERION_VALUES.map((val) => {
                          const display = CRITERION_DISPLAY[val]
                          return (
                            <button
                              key={val}
                              type="button"
                              disabled={isReadOnly}
                              className={unselectedClasses}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCriterionChange(index, val)
                              }}
                              aria-label={`Mark criterion ${index + 1} as ${display.label}`}
                            >
                              {display.label}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </td>

                {/* Empty notes cell */}
                <td className="py-2 px-4" style={{ width: "25%" }} />
              </tr>
            )
          })}
        </>
      )
    },
  )
CompositeEvaluationSubRows.displayName = "CompositeEvaluationSubRows"
